import React from 'react';
import { useEffect } from 'react';
import { fetchConnections, fetchRuns, fetchSyncSchemas } from '../../services/ApiService';
import dayjs from 'dayjs';
import { useUser } from '../../context/UserContext';
import { parseUtcDate } from '../../utils';
import Spinner from '../../components/Spinner';

interface Activity {
  name: string;
  date: string;
  // user: string;
}

const formatDate = (dateString: string) => {
  return dayjs(parseUtcDate(dateString)).format('DD/MM/YYYY HH:mm');
};

interface ActivityPanelProps {
  ready: boolean;
  onLoaded: () => void;
}

const ActivityPanel: React.FC<ActivityPanelProps> = ({ ready, onLoaded }) => {
  const [recentActivities, setActivities] = React.useState<Activity[]>([]);
  const { user } = useUser();

  useEffect(() => {
    const fetchActivities = async () => {
      if (!user?.administrationCodes?.length) {
        setActivities([]);
        onLoaded();
        return;
      }

      try {
        const [connectionsResp, schemasResp, runsResp] = await Promise.all([
          fetchConnections(5),
          fetchSyncSchemas(5),
          fetchRuns(5),
        ]);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- type is not narrowed here; typing it is a separate change
        const connections: Activity[] = (connectionsResp.data ?? []).map((record: any) => ({
          name: 'connections',
          date: record.updatedAt,
        }));

        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- type is not narrowed here; typing it is a separate change
        const schemas: Activity[] = (schemasResp.data ?? []).map((record: any) => ({
          name: 'synchronisation_schemas',
          date: record.updatedAt,
        }));

        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- type is not narrowed here; typing it is a separate change
        const runs: Activity[] = (runsResp.data ?? []).map((record: any) => ({
          name: 'runs',
          date: record.updatedAt ?? record.runDatetime,
        }));

        const combined = [...connections, ...schemas, ...runs]
          .filter((activity) => !!activity.date)
          .sort((a, b) => parseUtcDate(b.date).getTime() - parseUtcDate(a.date).getTime())
          .slice(0, 5);

        setActivities(combined);
      } catch (error) {
        console.error('Failed to load recent activities', error);
        setActivities([]);
      } finally {
        onLoaded();
      }
    };

    fetchActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.administrationCodes]);

  const activityFormatMap = {
    "connections": 'Nieuwe koppeling',
    "synchronisation_schemas": 'Nieuw synchronisatieschema',
    "runs": 'Geplande synchronisatie'
  }

  return (
    <div className="flex flex-col flex-1 px-4 py-5 bg-white rounded-2xl min-w-[240px] min-h-[300px] shadow-md">
      {/* Title */}
      <div className="text-lg font-semibold text-gray-900">Recente activiteit</div>

      {!ready ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <div className="overflow-x-auto mt-4">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100 text-gray-700 text-sm font-medium">
                <th className="py-2 px-4 text-left">Activiteit</th>
                <th className="py-2 px-4 text-left">Datum</th>
                {/* <th className="py-2 px-4 text-left">Gebruiker</th> */}
              </tr>
            </thead>
            <tbody>
              {recentActivities.length > 0 ? (
                recentActivities.map((activity, index) => (
                  <tr key={index} className="border-t text-gray-600 text-sm">
                    <td className="py-2 px-4">{activityFormatMap[activity.name as keyof typeof activityFormatMap]}</td>
                    <td className="py-2 px-4">{formatDate(activity.date)}</td>
                    {/* <td className="py-2 px-4">{activity.user}</td> */}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="py-3 text-center text-gray-500">
                    Geen recente activiteit
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ActivityPanel;
